/**
 * Trust Reconciliation Report - API Service
 *
 * HTTP client for Trust Reconciliation API endpoints
 */

import type {
  TrustReconciliationFlag,
  TrustTransactionResponse,
  TrustPivotResponse,
  TrustSummaryStats,
  TrustHealthCheck,
  ReportClient,
} from '@repo/types';

// Request parameter types
interface TrustFilters {
  clientIds?: string[];
  flags?: TrustReconciliationFlag[];
}

interface TrustTransactionParams extends TrustFilters {
  page?: number;
  limit?: number;
}

interface TrustPivotParams {
  clientIds?: string[];
  page?: number;
  limit?: number;
}

interface TrustExportParams extends TrustFilters {
  type: 'csv' | 'xls' | 'pdf';
  includeMetadata?: boolean;
}

import { api } from '@xtnd/web/shared/api';

class TrustReconciliationApiService {
  private readonly baseResource = '/reports/trust';

  /**
   * Build query string from parameters
   */
  private buildQueryString(params: Record<string, unknown>): string {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          // NestJS expects repeated keys without [] for array params
          value.forEach(item => searchParams.append(key, String(item)));
        } else {
          searchParams.set(key, String(value));
        }
      }
    });

    return searchParams.toString();
  }

  /**
   * Get trust transactions with pagination
   */
  async getTransactions(params: TrustTransactionParams): Promise<TrustTransactionResponse> {
    const queryString = this.buildQueryString(params as Record<string, unknown>);
    const endpoint = `${this.baseResource}/transactions${queryString ? `?${queryString}` : ''}`;

    return await api.get<TrustTransactionResponse>(endpoint);
  }

  /**
   * Get client pivot data
   */
  async getClientPivot(params: TrustPivotParams): Promise<TrustPivotResponse> {
    const queryString = this.buildQueryString(params as Record<string, unknown>);
    const endpoint = `${this.baseResource}/pivot${queryString ? `?${queryString}` : ''}`;

    return await api.get<TrustPivotResponse>(endpoint);
  }

  /**
   * Get summary statistics
   */
  async getSummaryStats(): Promise<TrustSummaryStats> {
    const endpoint = `${this.baseResource}/summary`;
    return await api.get<TrustSummaryStats>(endpoint);
  }

  /**
   * Get service health check
   */
  async getHealthCheck(): Promise<TrustHealthCheck> {
    const endpoint = `${this.baseResource}/health`;
    return await api.get<TrustHealthCheck>(endpoint);
  }

  /**
   * Export data in specified format
   */
  async exportData(params: TrustExportParams): Promise<void> {
    // Use fetch API to download the file
    const response = await fetch(
      `${import.meta.env.VITE_API_URL || 'http://localhost:8080'}${this.baseResource}/export`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify(params),
      }
    );

    if (!response.ok) {
      throw new Error(`Export failed: ${response.status} ${response.statusText}`);
    }

    // Get filename from response headers or use default with timestamp
    const contentDisposition = response.headers.get('content-disposition');
    const now = new Date();
    const timeStamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
    let filename = `trust-reconciliation-${timeStamp}.${params.type}`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    // Create blob and trigger download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    // Create temporary download link
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Get available clients for filtering
   */
  async getAvailableClients(): Promise<ReportClient[]> {
    const endpoint = `${this.baseResource}/clients`;
    return await api.get<ReportClient[]>(endpoint);
  }

  /**
   * Preflight check to ensure API is accessible
   */
  async checkConnection(): Promise<boolean> {
    try {
      await this.getHealthCheck();
      return true;
    } catch {
      return false;
    }
  }
}

// Export singleton instance
export const trustReconciliationApi = new TrustReconciliationApiService();

// Export types for use in components
export type {
  TrustFilters,
  TrustTransactionParams,
  TrustPivotParams,
  TrustExportParams,
};

// Re-export shared types from @repo/types
export type {
  TrustTransactionResponse,
  TrustPivotResponse,
  TrustSummaryStats,
  TrustHealthCheck,
  TrustReconciliationFlag,
  ReconciliationStatus,
  ConfidenceLevel,
  TrustTransaction,
  TrustPivotClient,
  ReportClient,
} from '@repo/types';

